import React, { useState, useEffect } from 'react';
import { ecommerceAPI } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Skeleton } from '../../components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { ShoppingBag, Plus, Package, List } from 'lucide-react';
import { toast } from 'sonner';

const STATUS_COLORS = { pending: '#9A6B1A', processing: '#1E5A8A', shipped: '#B89B5E', delivered: '#1F7A5A', cancelled: '#B3261E' };

export default function AdminEcommerce() {
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showProduct, setShowProduct] = useState(false);
  const [productForm, setProductForm] = useState({ name: '', description: '', type: 'digital', price: '', currency: 'GBP', image_url: '', inventory: '' });

  useEffect(() => { loadAll(); }, []);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [pRes, oRes] = await Promise.all([
        ecommerceAPI.listProducts().catch(() => ({ data: { products: [] } })),
        ecommerceAPI.listOrders().catch(() => ({ data: { orders: [] } })),
      ]);
      setProducts(pRes.data.products || []);
      setOrders(oRes.data.orders || []);
    } catch { toast.error('Failed to load store data'); }
    finally { setLoading(false); }
  };

  const createProduct = async () => {
    if (!productForm.name || !productForm.price) { toast.error('Name and price required'); return; }
    try {
      await ecommerceAPI.createProduct({ ...productForm, price: parseFloat(productForm.price), inventory: productForm.inventory ? parseInt(productForm.inventory, 10) : null });
      toast.success('Product created');
      setShowProduct(false);
      setProductForm({ name: '', description: '', type: 'digital', price: '', currency: 'GBP', image_url: '', inventory: '' });
      loadAll();
    } catch { toast.error('Failed to create product'); }
  };

  const updateOrderStatus = async (id, status) => {
    try { await ecommerceAPI.updateOrderStatus(id, status); toast.success('Order updated'); loadAll(); }
    catch { toast.error('Failed'); }
  };

  const revenue = orders.filter(o => o.status !== 'cancelled').reduce((s, o) => s + (o.total || 0), 0);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><ShoppingBag className="w-6 h-6 text-[#B89B5E]" /> E-Commerce Store</h1>
          <p className="text-sm text-[#5B616E] mt-1">Digital products, tickets, and branded merchandise</p>
        </div>
        <Button onClick={() => setShowProduct(true)} className="bg-[#121316] text-white hover:bg-[#1B1D22]">
          <Plus className="w-4 h-4 mr-2" /> New Product
        </Button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {[
          { label: 'Products', value: products.length, color: '#1E5A8A', icon: Package },
          { label: 'Orders', value: orders.length, color: '#B89B5E', icon: List },
          { label: 'Revenue', value: `£${revenue.toFixed(2)}`, color: '#1F7A5A', icon: ShoppingBag },
        ].map(k => (
          <Card key={k.label} className="bg-white border-[#E7E0D6]">
            <CardContent className="pt-4">
              <div className="flex items-center gap-2 mb-1"><k.icon className="w-4 h-4" style={{ color: k.color }} /><span className="text-xs text-[#5B616E]">{k.label}</span></div>
              <p className="text-2xl font-bold" style={{ color: k.color }}>{k.value}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <Tabs defaultValue="products">
        <TabsList><TabsTrigger value="products">Products</TabsTrigger><TabsTrigger value="orders">Orders</TabsTrigger></TabsList>

        <TabsContent value="products" className="mt-4">
          {loading ? (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">{[1,2,3].map(i => <Skeleton key={i} className="h-36" />)}</div>
          ) : products.length === 0 ? (
            <div className="text-center py-12 text-[#5B616E]"><Package className="w-12 h-12 mx-auto mb-3 opacity-20" /><p>No products yet. Create your first product.</p></div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {products.map(p => (
                <Card key={p.id} className="bg-white border-[#E7E0D6]">
                  {p.image_url && <img src={p.image_url} alt={p.name} className="w-full h-36 object-cover rounded-t-xl" />}
                  <CardContent className="p-4">
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="font-semibold text-sm">{p.name}</h3>
                        <Badge variant="outline" className="text-[10px] mt-0.5">{p.type}</Badge>
                      </div>
                      <span className="font-bold text-[#B89B5E]">{p.currency} {p.price}</span>
                    </div>
                    <p className="text-xs text-[#5B616E] mt-1 line-clamp-2">{p.description}</p>
                    {p.inventory !== null && p.inventory !== undefined && (
                      <p className="text-[10px] text-[#5B616E] mt-1">Stock: {p.inventory}</p>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="orders" className="mt-4">
          <Card className="bg-white border-[#E7E0D6]">
            <CardContent className="pt-4">
              {orders.length === 0 ? (
                <p className="text-sm text-[#5B616E] text-center py-8">No orders yet.</p>
              ) : (
                <div className="space-y-2">
                  {orders.map(o => (
                    <div key={o.id} className="flex items-center justify-between p-3 border border-[#E7E0D6] rounded-lg">
                      <div>
                        <p className="text-sm font-medium">{o.order_number} — {o.product_name}</p>
                        <p className="text-xs text-[#5B616E]">Qty {o.quantity} · {o.currency} {o.total?.toFixed(2)}</p>
                        <p className="text-[10px] text-[#5B616E]">{o.created_at ? new Date(o.created_at).toLocaleDateString() : ''}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge style={{ background: `${STATUS_COLORS[o.status] || '#5B616E'}20`, color: STATUS_COLORS[o.status] || '#5B616E' }} className="text-[10px]">{o.status}</Badge>
                        {o.status === 'pending' && (
                          <Select value={o.status} onValueChange={v => updateOrderStatus(o.id, v)}>
                            <SelectTrigger className="h-7 w-28 text-xs"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {['pending','processing','shipped','delivered','cancelled'].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Product dialog */}
      <Dialog open={showProduct} onOpenChange={setShowProduct}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>New Product</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <Input placeholder="Product name *" value={productForm.name} onChange={e => setProductForm(f => ({ ...f, name: e.target.value }))} />
            <Input placeholder="Description" value={productForm.description} onChange={e => setProductForm(f => ({ ...f, description: e.target.value }))} />
            <div className="grid grid-cols-2 gap-3">
              <Select value={productForm.type} onValueChange={v => setProductForm(f => ({ ...f, type: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {['digital', 'physical', 'ticket', 'subscription'].map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={productForm.currency} onValueChange={v => setProductForm(f => ({ ...f, currency: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{['GBP', 'USD', 'EUR'].map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
              <Input type="number" placeholder="Price *" value={productForm.price} onChange={e => setProductForm(f => ({ ...f, price: e.target.value }))} />
              <Input type="number" placeholder="Inventory (optional)" value={productForm.inventory} onChange={e => setProductForm(f => ({ ...f, inventory: e.target.value }))} />
            </div>
            <Input placeholder="Image URL" value={productForm.image_url} onChange={e => setProductForm(f => ({ ...f, image_url: e.target.value }))} />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowProduct(false)}>Cancel</Button>
            <Button onClick={createProduct} className="bg-[#121316] text-white hover:bg-[#1B1D22]">Create Product</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
